import { useCallback, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import { getBdcCreditableItems, searchBdcCreditableInvoices } from "../../../services/bdcCreditNote";
import { BdcCreditableInvoice, BdcCreditableItem } from "../../../types/bdc.type";
import { useDebounce } from "../../../utils/hooks/useDebounce";
import { baht, pcs, rowsOf, thaiDate } from "../format";
import Tag from "../Tag";
import { CnDraftLine, lineFromCreditable, lineKey, parseQty } from "./cn";

/** How far back the INV search looks by default from the CN date. */
const SEARCH_DAYS_BACK = 45;

/**
 * เพิ่มสินค้าจาก INV — search the DC's INVs that can still be credited, pick one, type the pieces
 * per product (up to what it has left), add them to the CN. Products already on the CN show their
 * current quantity, and adding again replaces it.
 */
export default function InvoicePicker({
  dcCode,
  noteDate,
  lines,
  initialInvoice,
  onAdd,
}: Readonly<{
  dcCode: string;
  noteDate: string;
  lines: CnDraftLine[];
  /** Open this INV straight away (ทะเบียน INV › สร้าง CN). */
  initialInvoice?: Pick<BdcCreditableInvoice, "invoice_no" | "po_number" | "is_test" | "invoice_date"> | null;
  onAdd: (lines: CnDraftLine[], isTest: boolean) => void;
}>) {
  const [dateFrom, setDateFrom] = useState(() => dayjs(noteDate).subtract(SEARCH_DAYS_BACK, "day").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(noteDate);
  const [searchText, setSearchText] = useState("");
  const search = useDebounce(searchText, 400);
  const [invoices, setInvoices] = useState<BdcCreditableInvoice[]>([]);
  const [searching, setSearching] = useState(false);

  const [invoice, setInvoice] = useState<Pick<BdcCreditableInvoice, "invoice_no" | "po_number" | "is_test"> | null>(
    initialInvoice ?? null,
  );
  const [items, setItems] = useState<BdcCreditableItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [qtyText, setQtyText] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialInvoice) setInvoice(initialInvoice);
  }, [initialInvoice]);

  const runSearch = useCallback(async () => {
    if (!dcCode) {
      setInvoices([]);
      return;
    }
    setSearching(true);
    try {
      const {
        data: { data },
      } = await searchBdcCreditableInvoices({
        dc_code: dcCode,
        date_from: dateFrom,
        date_to: dateTo,
        ...(search.trim() ? { search: search.trim() } : {}),
      });
      setInvoices(rowsOf(data));
    } catch {
      setInvoices([]);
    } finally {
      setSearching(false);
    }
  }, [dcCode, dateFrom, dateTo, search]);

  useEffect(() => {
    runSearch();
  }, [runSearch]);

  useEffect(() => {
    if (!invoice) {
      setItems([]);
      return;
    }
    let active = true;
    setItemsLoading(true);
    getBdcCreditableItems(invoice.invoice_no)
      .then(({ data: { data } }) => {
        if (!active) return;
        const list = rowsOf(data);
        setItems(list);
        // start from what the CN already holds for this INV
        const current: Record<string, string> = {};
        for (const item of list) {
          const line = lines.find((l) => l.key === lineKey(invoice.invoice_no, item.barcode));
          current[item.barcode] = line ? line.qty : "";
        }
        setQtyText(current);
      })
      .catch(() => {
        if (active) setItems([]);
      })
      .finally(() => {
        if (active) setItemsLoading(false);
      });
    return () => {
      active = false;
    };
    // the typed quantities start from the lines once per INV, not on every edit of the CN
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  const pickDate = (setter: (value: string) => void) => (value: Dayjs | null) => {
    if (value && value.isValid()) setter(value.format("YYYY-MM-DD"));
  };

  /** What this INV × product may take on this CN: the server's figure plus what the CN holds already. */
  const availableOf = (item: BdcCreditableItem): number => {
    const line = invoice ? lines.find((l) => l.key === lineKey(invoice.invoice_no, item.barcode)) : undefined;
    if (line && line.available !== null) return line.available;
    return item.creditable_qty;
  };

  const typed = items
    .map((item) => ({ item, text: qtyText[item.barcode] ?? "" }))
    .filter(({ text }) => text.trim() !== "");
  const badTyped = typed.filter(({ text }) => parseQty(text) === null);
  const overTyped = typed.filter(({ item, text }) => {
    const qty = parseQty(text);
    return qty !== null && qty > availableOf(item);
  });

  const add = () => {
    if (!invoice || badTyped.length > 0) return;
    const added = typed.map(({ item, text }) => {
      const line = lineFromCreditable(invoice, item, parseQty(text) ?? 0);
      const existing = lines.find((l) => l.key === line.key);
      return existing ? { ...existing, qty: line.qty } : { ...line, available: availableOf(item) };
    });
    onAdd(added, invoice.is_test);
    setInvoice(null);
  };

  if (!dcCode) {
    return <Alert severity="info">เลือก DC ก่อน แล้วจึงเลือก INV ที่จะลดหนี้</Alert>;
  }

  return (
    <Stack gap={1.5} data-testid="bdc-cn-picker">
      {!invoice && (
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
              <DatePicker
                label="วันที่ INV ตั้งแต่"
                value={dayjs(dateFrom)}
                format="DD/MM/YYYY"
                onChange={pickDate(setDateFrom)}
                slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
              />
              <DatePicker
                label="ถึง"
                value={dayjs(dateTo)}
                format="DD/MM/YYYY"
                onChange={pickDate(setDateTo)}
                slotProps={{ textField: { size: "small", sx: { width: 170 } } }}
              />
              <TextField
                size="small"
                placeholder="เลข INV / PO"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{ width: 200 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Stack>
          </LocalizationProvider>
          {searching && <LinearProgress />}
          <Box sx={{ overflow: "auto", maxHeight: 280 }}>
            <table className="bdc-t">
              <thead>
                <tr>
                  <th>เลข INV</th>
                  <th>วันที่</th>
                  <th>PO</th>
                  <th className="n">รวม</th>
                  <th className="n">CN แล้ว</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_uuid}>
                    <td className="mono">
                      <b>{inv.invoice_no}</b> {inv.is_test && <Tag tone="warn">ทดสอบ</Tag>}
                    </td>
                    <td>{thaiDate(inv.invoice_date)}</td>
                    <td className="mono">{inv.po_number}</td>
                    <td className="n">{baht(inv.grand_total)}</td>
                    <td className="n">{inv.credited_amount ? baht(inv.credited_amount) : "—"}</td>
                    <td>
                      <Button size="small" variant="outlined" onClick={() => setInvoice(inv)}>
                        เลือก
                      </Button>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && !searching && (
                  <tr>
                    <td colSpan={6} className="sub" style={{ textAlign: "center", padding: 16 }}>
                      ไม่มี INV ของ {dcCode} ที่ยังลดหนี้ได้ในช่วงนี้
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
        </>
      )}

      {invoice && (
        <>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography fontWeight={600}>
              INV <span className="mono">{invoice.invoice_no}</span> · PO <span className="mono">{invoice.po_number}</span>
            </Typography>
            {invoice.is_test && <Tag tone="warn">ทดสอบ</Tag>}
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => setInvoice(null)}>
              เลือก INV อื่น
            </Button>
          </Stack>
          {itemsLoading && <LinearProgress />}
          <Box sx={{ overflow: "auto", maxHeight: 360 }}>
            <table className="bdc-t" data-testid="bdc-cn-creditable">
              <thead>
                <tr>
                  <th>สินค้า</th>
                  <th className="n">ราคา/ชิ้น</th>
                  <th className="n">ใน INV</th>
                  <th className="n">CN แล้ว</th>
                  <th className="n">ลดได้</th>
                  <th style={{ width: 120 }}>จำนวนที่ลด</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const text = qtyText[item.barcode] ?? "";
                  const qty = parseQty(text);
                  const available = availableOf(item);
                  const bad = text.trim() !== "" && qty === null;
                  const over = qty !== null && qty > available;
                  return (
                    <tr key={item.barcode}>
                      <td>
                        {item.name} {item.size ?? ""}
                        <div className="sub mono">
                          {item.item_code} · {item.barcode}
                        </div>
                      </td>
                      <td className="n">{baht(item.unit_price)}</td>
                      <td className="n">{pcs(item.invoiced_qty)}</td>
                      <td className="n">{pcs(item.credited_qty)}</td>
                      <td className="n">
                        <b>{pcs(available)}</b>
                      </td>
                      <td>
                        <TextField
                          size="small"
                          value={text}
                          disabled={available <= 0 && text === ""}
                          onChange={(e) => setQtyText((current) => ({ ...current, [item.barcode]: e.target.value }))}
                          error={bad || over}
                          helperText={bad ? "จำนวนเต็ม > 0" : over ? "เกินยอดที่ลดได้" : undefined}
                          slotProps={{ htmlInput: { inputMode: "numeric", "aria-label": `จำนวนที่ลด ${item.item_code}` } }}
                        />
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && !itemsLoading && (
                  <tr>
                    <td colSpan={6} className="sub" style={{ textAlign: "center", padding: 16 }}>
                      INV นี้ไม่มีสินค้าที่ลดหนี้ได้
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
          {overTyped.length > 0 && (
            <Alert severity="warning">
              {overTyped.map(({ item }) => item.item_code).join(", ")} เกินยอดที่ลดได้ — บันทึกร่างได้ แต่ออก CN ไม่ได้จนกว่าจะแก้
            </Alert>
          )}
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" disabled={typed.length === 0 || badTyped.length > 0} onClick={add}>
              เพิ่มเข้า CN ({pcs(typed.length)} รายการ)
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  );
}
